import { CASES } from './constants';
import {
    PAGINATION_STORAGE_KEYS,
    DEFAULT_PAGE_SIZE_OPTIONS,
    getStoredItemsPerPage,
    getInitialPaginationRange,
    withPreservedPagination,
    persistItemsPerPage,
} from 'datastage/utils/paginationPreferences.js';

const getInitialProductsSort = () => {
    const saved = localStorage.getItem('productSort');
    return saved ? JSON.parse(saved) : ['id', 'ASC'];
};

/**
 * Gets the initial items per page from localStorage.
 * Falls back to 20 (Products default) if not found.
 */
const getInitialItemsPerPage = () => {
    return getStoredItemsPerPage(PAGINATION_STORAGE_KEYS.products, 20);
};

// Initialize with stored preference
const initialItemsPerPage = getInitialItemsPerPage();

/**
 * Initial state for the products slice.
 *
 * Key state values:
 * - products: Array of product objects for the grid
 * - filterForm: Search form field values
 * - isDetailsOpen: Boolean to control details drawer visibility
 * - refreshProducts: Boolean flag to trigger grid refresh from other components
 * - itemsPerPage: User's persisted page size preference (from localStorage)
 */
const initialState = {
    products: [],
    filterForm: {
        productId: '',
        productTitle: '',
        classFilter: '',
        loadStatus: '',
        legalStatusId: '',
        startYear: '',
        endYear: '',
        foxFinancialTitleId: '',
    },
    classOptions: [
        { label: 'BONUS', value: 'BONUS' },
        { label: 'COMPONENT', value: 'COMPONENT' },
        { label: 'EDUCATIONAL', value: 'EDUCATIONAL' },
        { label: 'FEATURE', value: 'FEATURE' },
        { label: 'FEATURETTE', value: 'FEATURETTE' },
        { label: 'HOME VIDEO', value: 'HOME VIDEO' },
        { label: 'INTERSTITIAL', value: 'INTERSTITIAL' },
        { label: 'MINI SERIES', value: 'MINI SERIES' },
        { label: 'PILOT', value: 'PILOT' },
        { label: 'SERIES', value: 'SERIES' },
        { label: 'SHORT', value: 'SHORT' },
        { label: 'SPECIAL', value: 'SPECIAL' },
        { label: 'TV MOVIE', value: 'TV MOVIE' },
    ],
    loadStatusOptions: [
        { label: 'READY', value: 'READY' },
        { label: 'COMPLETE', value: 'COMPLETE' },
        { label: 'ERROR', value: 'ERROR' },
        { label: 'SKIPPED', value: 'SKIPPED' },
        { label: 'HOLD', value: 'HOLD' },
        { label: 'IN_PROGRESS', value: 'IN_PROGRESS' },
        { label: 'REPROCESS', value: 'REPROCESS' },
    ],
    legalStatusOptions: [
        { label: 'CONFIRMED', value: 1 },
        { label: 'DRAFT', value: 2 },
        { label: 'PENDING', value: 3 },
        { label: 'PENDING WITH FEATURE CAVEAT', value: 4 },
        { label: 'PRELIMINARY', value: 5 },
        { label: 'PROCESSING', value: 7 },
    ],
    totalPages: 0,
    totalItems: 0,
    currentPage: 0,
    filterParam: '',
    productRange: getInitialPaginationRange(initialItemsPerPage),
    productSort: getInitialProductsSort(),
    defaultFilterRange: getInitialPaginationRange(initialItemsPerPage),
    itemsPerPage: initialItemsPerPage,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    isDetailsOpen: false,
    details: [],
    // Flag to trigger grid refresh from other components
    refreshProducts: false,
};

export function products(state = initialState, action) {
    const { type, payload } = action;
    switch (type) {
        case CASES.UPDATE_PRODUCTS: {
            // Persist sort preference to localStorage
            if (payload.productSort) {
                localStorage.setItem('productSort', JSON.stringify(payload.productSort));
            }
            // Persist page size preference to localStorage
            if (payload.itemsPerPage !== undefined) {
                persistItemsPerPage(PAGINATION_STORAGE_KEYS.products, payload.itemsPerPage);
            }
            return { ...state, ...payload };
        }
        case CASES.UPDATE_PRODUCTS_SEARCH_FORM: {
            const { data, key } = payload;
            return { ...state, [key]: { ...state[key], ...data } };
        }
        case CASES.CLEAR_PRODUCTS: {
            // Preserve pagination preferences when clearing
            return withPreservedPagination(
                state,
                {
                    ...initialState,
                    productSort: state.productSort,
                },
                ['productRange', 'defaultFilterRange']
            );
        }
        default:
            return state;
    }
}
